import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { gameAPI, SaveData, LeaderboardEntry } from '../api/GameAPI';

/**
 * Main menu scene with save/load and leaderboard
 */
export class MenuScene extends Phaser.Scene {
  private titleText!: Phaser.GameObjects.Text;
  private menuItems: Phaser.GameObjects.Text[] = [];
  private selectedIndex: number = 0;
  private currentView: 'main' | 'saves' | 'leaderboard' | 'register' = 'main';
  private saves: SaveData[] = [];
  private leaderboard: LeaderboardEntry[] = [];
  private inputText: string = '';
  private isRegistered: boolean = false;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0x0a0a1e);

    // Check if registered
    this.isRegistered = !!gameAPI.getUsername();

    // Title
    this.titleText = this.add.text(GAME_CONFIG.WIDTH / 2, 30, 'COSMIC CADET', {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#00ff88',
    });
    this.titleText.setOrigin(0.5);

    // Subtitle
    this.add.text(GAME_CONFIG.WIDTH / 2, 50, 'A Commander Keen Tribute', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#888888',
    }).setOrigin(0.5);

    // Show main menu
    this.showMainMenu();

    // Setup input
    this.setupInput();
  }

  private setupInput(): void {
    if (!this.input.keyboard) return;

    this.input.keyboard.on('keydown-UP', () => this.selectPrev());
    this.input.keyboard.on('keydown-DOWN', () => this.selectNext());
    this.input.keyboard.on('keydown-ENTER', () => this.confirmSelection());
    this.input.keyboard.on('keydown-ESC', () => this.goBack());
    this.input.keyboard.on('keydown-SPACE', () => this.confirmSelection());

    // Text input for registration
    this.input.keyboard.on('keydown', (event: KeyboardEvent) => {
      if (this.currentView === 'register') {
        if (event.key === 'Backspace') {
          this.inputText = this.inputText.slice(0, -1);
          this.updateRegisterView();
        } else if (event.key.length === 1 && this.inputText.length < 20) {
          this.inputText += event.key;
          this.updateRegisterView();
        }
      }
    });
  }

  private clearMenu(): void {
    this.menuItems.forEach(item => item.destroy());
    this.menuItems = [];
  }

  private showMainMenu(): void {
    this.clearMenu();
    this.currentView = 'main';
    this.selectedIndex = 0;

    const items = this.isRegistered
      ? ['NEW GAME', 'LOAD GAME', 'LEADERBOARD', 'CONTROLS']
      : ['NEW GAME', 'REGISTER', 'LEADERBOARD', 'CONTROLS'];

    const startY = 90;
    items.forEach((text, index) => {
      const item = this.add.text(GAME_CONFIG.WIDTH / 2, startY + index * 25, text, {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#ffffff',
      });
      item.setOrigin(0.5);
      this.menuItems.push(item);
    });

    // Show username if registered
    if (this.isRegistered) {
      this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT - 20, `Player: ${gameAPI.getUsername()}`, {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: '#555555',
      }).setOrigin(0.5);
    }

    this.updateSelection();
  }

  private async showSavesMenu(): Promise<void> {
    this.clearMenu();
    this.currentView = 'saves';
    this.selectedIndex = 0;

    // Loading text
    const loadingText = this.add.text(GAME_CONFIG.WIDTH / 2, 100, 'Loading saves...', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#888888',
    });
    loadingText.setOrigin(0.5);

    try {
      this.saves = await gameAPI.getSaves();
    } catch {
      this.saves = [];
    }

    loadingText.destroy();

    // Show save slots
    const startY = 80;
    for (let i = 0; i < 3; i++) {
      const save = this.saves.find(s => s.slot === i);
      let text: string;
      if (save) {
        text = `SLOT ${i + 1}: Level ${save.level} - Score ${save.score}`;
      } else {
        text = `SLOT ${i + 1}: Empty`;
      }
      const item = this.add.text(GAME_CONFIG.WIDTH / 2, startY + i * 25, text, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: save ? '#00ff88' : '#666666',
      });
      item.setOrigin(0.5);
      this.menuItems.push(item);
    }

    // Back option
    const backItem = this.add.text(GAME_CONFIG.WIDTH / 2, startY + 100, 'BACK', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
    });
    backItem.setOrigin(0.5);
    this.menuItems.push(backItem);

    this.updateSelection();
  }

  private async showLeaderboard(): Promise<void> {
    this.clearMenu();
    this.currentView = 'leaderboard';
    this.selectedIndex = 0;

    // Loading
    const loadingText = this.add.text(GAME_CONFIG.WIDTH / 2, 100, 'Loading leaderboard...', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#888888',
    });
    loadingText.setOrigin(0.5);

    try {
      const result = await gameAPI.getLeaderboard(10);
      this.leaderboard = result.entries;
    } catch {
      this.leaderboard = [];
    }

    loadingText.destroy();

    // Header
    this.add.text(GAME_CONFIG.WIDTH / 2, 70, 'HIGH SCORES', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffff00',
    }).setOrigin(0.5);

    // Entries
    const startY = 90;
    if (this.leaderboard.length === 0) {
      this.add.text(GAME_CONFIG.WIDTH / 2, startY, 'No scores yet!', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#666666',
      }).setOrigin(0.5);
    } else {
      this.leaderboard.forEach((entry, index) => {
        const rank = `${index + 1}.`.padStart(3);
        const name = entry.username.padEnd(12).slice(0, 12);
        const score = entry.score.toString().padStart(8);
        const text = `${rank} ${name} ${score}`;

        this.add.text(GAME_CONFIG.WIDTH / 2, startY + index * 12, text, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: index < 3 ? '#ffff00' : '#ffffff',
        }).setOrigin(0.5);
      });
    }

    // Back option
    const backItem = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT - 30, 'BACK', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
    });
    backItem.setOrigin(0.5);
    this.menuItems.push(backItem);

    this.updateSelection();
  }

  private showRegisterView(): void {
    this.clearMenu();
    this.currentView = 'register';
    this.inputText = '';

    this.add.text(GAME_CONFIG.WIDTH / 2, 80, 'Enter your name:', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ffffff',
    }).setOrigin(0.5);

    // Input box
    const inputBox = this.add.text(GAME_CONFIG.WIDTH / 2, 110, '_', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#00ff88',
      backgroundColor: '#333333',
      padding: { x: 10, y: 5 },
    });
    inputBox.setOrigin(0.5);
    this.menuItems.push(inputBox);

    // Instructions
    this.add.text(GAME_CONFIG.WIDTH / 2, 150, 'Press ENTER to confirm', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#888888',
    }).setOrigin(0.5);

    this.add.text(GAME_CONFIG.WIDTH / 2, 165, 'Press ESC to cancel', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#888888',
    }).setOrigin(0.5);
  }

  private updateRegisterView(): void {
    if (this.menuItems[0]) {
      this.menuItems[0].setText(this.inputText + '_');
    }
  }

  private showControls(): void {
    this.clearMenu();

    const controls = [
      'CONTROLS',
      '',
      'Arrow Keys / WASD - Move',
      'Space / Up - Jump',
      'Shift - Pogo Jump',
      '',
      'Collect coins and floppy disks!',
      'Avoid or stomp enemies!',
      '',
      'Press any key to continue...',
    ];

    const startY = 70;
    controls.forEach((text, index) => {
      const color = index === 0 ? '#ffff00' : '#ffffff';
      this.add.text(GAME_CONFIG.WIDTH / 2, startY + index * 14, text, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color,
      }).setOrigin(0.5);
    });

    // Wait for any key
    this.input.keyboard?.once('keydown', () => {
      this.showMainMenu();
    });
  }

  private selectPrev(): void {
    if (this.currentView === 'register') return;
    this.selectedIndex = Math.max(0, this.selectedIndex - 1);
    this.updateSelection();
  }

  private selectNext(): void {
    if (this.currentView === 'register') return;
    this.selectedIndex = Math.min(this.menuItems.length - 1, this.selectedIndex + 1);
    this.updateSelection();
  }

  private updateSelection(): void {
    this.menuItems.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.setColor('#00ff88');
        item.setText('> ' + item.text.replace(/^> /, '') + ' <');
      } else {
        item.setColor('#ffffff');
        item.setText(item.text.replace(/^> /, '').replace(/ <$/, ''));
      }
    });
  }

  private async confirmSelection(): Promise<void> {
    if (this.currentView === 'main') {
      const items = this.isRegistered
        ? ['newgame', 'load', 'leaderboard', 'controls']
        : ['newgame', 'register', 'leaderboard', 'controls'];

      switch (items[this.selectedIndex]) {
        case 'newgame':
          this.scene.start('GameScene');
          break;
        case 'load':
          await this.showSavesMenu();
          break;
        case 'register':
          this.showRegisterView();
          break;
        case 'leaderboard':
          await this.showLeaderboard();
          break;
        case 'controls':
          this.showControls();
          break;
      }
    } else if (this.currentView === 'saves') {
      if (this.selectedIndex === 3) {
        this.showMainMenu();
      } else {
        const save = this.saves.find(s => s.slot === this.selectedIndex);
        if (save) {
          // Load saved game
          this.scene.start('GameScene', { loadSave: save });
        }
      }
    } else if (this.currentView === 'leaderboard') {
      this.showMainMenu();
    } else if (this.currentView === 'register') {
      if (this.inputText.trim().length >= 1) {
        try {
          await gameAPI.register(this.inputText.trim());
          this.isRegistered = true;
          this.showMainMenu();
        } catch (error) {
          console.error('Registration failed:', error);
        }
      }
    }
  }

  private goBack(): void {
    if (this.currentView !== 'main') {
      this.showMainMenu();
    }
  }
}
